import { Size } from '@ps/web-core-enums';
import { Translation, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React from 'react';
import { FormattedNumber } from 'react-intl';
import { useSelector } from 'react-redux';
import type { Column } from 'react-table';

import { select } from '@swf/core/store';
import { Table, useHomeGamesWidget } from '@swf/poker';

import type { ClubSeasonStats } from '../../../../types/common';
import CurrencySwitch from '../CurrencySwitch/CurrencySwitch';
import NoStandings from '../NoStandings/NoStandings';

import {
  TXTACL_Player,
  TXTCLI_KO__tab,
  TXTCLI_Points,
  TXTCLI_Rank,
  TXTCLI_Standings,
  TXTMOB_Wins,
  TXTWCL_Played_1,
  TXTWCL_Points_are_only_awarded_in_tou_ELL
} from './StandingsList.messages.json';
import styles from './StandingsList.scss';

const columns: Column<ClubSeasonStats>[] = [
  { Header: <Translation {...TXTCLI_Rank} />, accessor: 'rank' },
  { Header: <Translation {...TXTACL_Player} />, accessor: 'userId' },
  { Header: <Translation {...TXTWCL_Played_1} />, accessor: 'qualifiedGames' },
  { Header: <Translation {...TXTMOB_Wins} />, accessor: 'goldFinishes' },
  { Header: <Translation {...TXTCLI_KO__tab} />, accessor: 'knockouts' },
  { Header: <Translation {...TXTCLI_Points} />, accessor: 'points' }
];

const columnsMobile: Column<ClubSeasonStats>[] = [
  { Header: <Translation {...TXTCLI_Rank} />, accessor: 'rank' },
  { Header: '', accessor: 'userId' },
  { Header: <Translation {...TXTCLI_Points} />, accessor: 'points' }
];

export interface Props {
  seasonName: string;
  loading: boolean;
  clubName: string;
  stats: ClubSeasonStats[];
}

const StandingsList: React.FC<Props> = ({ clubName, loading, seasonName, stats = [] }) => {
  const { widgetSelect } = useHomeGamesWidget();
  const currentUserId = useSelector(select.account.getUserId);
  const { standingsPlayMoneyFlag } = useSelector(widgetSelect.uiLayout);
  const { greaterThan } = useScreenSize();
  const isMobile = !greaterThan(Size.XXSmall);
  const noStats = isEmpty(stats);

  const formattedStats = stats.map((stat) => ({
    ...stat,
    knockouts: (
      <FormattedNumber
        value={Number(stat.knockouts) / 1000}
        style="decimal"
        maximumFractionDigits={0}
      />
    ),
    points: (
      <FormattedNumber
        value={Number(stat.points) / 1000}
        style="decimal"
        minimumFractionDigits={Number(stat.points) % 1 !== 0 ? 2 : 0}
      />
    )
  }));

  return (
    <>
      <CurrencySwitch />
      <div className={styles.standingsTitle}>
        {`${clubName} ${seasonName} `}
        <Translation tagName="span" {...TXTCLI_Standings} />
      </div>
      <div className={styles.standingsText}>
        <Translation {...TXTWCL_Points_are_only_awarded_in_tou_ELL} values={{ 0: 4 }} />
      </div>
      {noStats ? (
        <NoStandings isPlayMoney={standingsPlayMoneyFlag} />
      ) : (
        <div className={styles.standingsTable}>
          <Table
            columns={isMobile ? columnsMobile : columns}
            data={formattedStats}
            sortable={false}
            loading={loading}
            rowClassName={(row) =>
              classNames(styles.standingsTableRow, {
                [styles.standingsTableRowGray]: row.rank === '-',
                [styles.standingsTableRowBold]: row.userId === currentUserId
              })
            }
          />
        </div>
      )}
    </>
  );
};

export default StandingsList;
